import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Clock,
  Search,
  ChevronRight,
  User,
  FileText,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Square,
  CheckSquare,
  Loader2,
  Calendar,
  RotateCcw,
  BarChart3,
  Timer,
  Zap,
  Inbox,
  FileQuestion,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';
import { KycStatusBadge } from '../../components/ui/KycStatusBadge';
import { getKycTypeLabel } from '../../lib/terminology';

interface KycPendingItem {
  id: string;
  virtualAccountId: string;
  userName: string;
  userEmail: string;
  userDocument?: string;
  kycType: string;
  status: string;
  submittedAt: string;
  documentsCount: number;
  waitingTime: string;
  waitingDays: number;
  waitingHours: number;
  isResubmission?: boolean;
  resubmissionCount?: number;
  assignedAnalystId?: string;
  assignedAnalystName?: string;
  slaDeadline?: string;
  slaStatus?: 'ON_TIME' | 'WARNING' | 'BREACHED';
  slaRemainingHours?: number;
  priority?: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
}

interface KycPendingList {
  items: KycPendingItem[];
  total: number;
  page: number;
  limit: number;
}

interface KycStats {
  pending: number;
  inReview: number;
  approvedToday: number;
  rejectedToday: number;
  avgReviewTime: string;
  pendingOrigin: number;
  pendingDestination: number;
  resubmissionsCount: number;
  oldestPendingDays: number;
  slaBreachedCount: number;
  slaWarningCount: number;
  slaOnTimeCount: number;
  avgSlaCompliancePercent: number;
  awaitingSubmission: number;
}

interface AwaitingSubmissionItem {
  virtualAccountId: string;
  userName: string;
  userEmail: string;
  userDocument: string;
  contractStatus: string;
  kycOriginStatus: string;
  kycDestinationStatus: string;
  createdAt: string;
  waitingTime: string;
  waitingDays: number;
  hasOriginSubmission: boolean;
  hasDestinationSubmission: boolean;
}

interface AwaitingSubmissionList {
  items: AwaitingSubmissionItem[];
  total: number;
  page: number;
  limit: number;
}

const statusConfig: Record<string, { label: string; color: string; bgColor: string; borderColor: string }> = {
  SUBMITTED: { label: 'Aguardando', color: 'text-yellow-400', bgColor: 'bg-yellow-500/10', borderColor: 'border-yellow-500/50' },
  PENDING_REVIEW: { label: 'Em Análise', color: 'text-blue-400', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/50' },
};

export function KycQueue() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'pending' | 'awaiting'>('pending');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [kycTypeFilter, setKycTypeFilter] = useState<string>('');
  const [slaFilter, setSlaFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [awaitingPage, setAwaitingPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBatchRejectModal, setShowBatchRejectModal] = useState(false);
  const [batchRejectReason, setBatchRejectReason] = useState('');
  const limit = 12;

  // Buscar lista de KYCs pendentes
  const { data: pendingList, isLoading, refetch } = useQuery<KycPendingList>({
    queryKey: ['kyc-pending', page, statusFilter, kycTypeFilter, slaFilter, priorityFilter, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (statusFilter) params.append('status', statusFilter);
      if (kycTypeFilter) params.append('kycType', kycTypeFilter);
      if (slaFilter) params.append('slaStatus', slaFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (search) params.append('search', search);
      
      const response = await api.get(`/admin/kyc/pending?${params.toString()}`);
      return response.data;
    },
  });

  // Buscar estatísticas
  const { data: stats } = useQuery<KycStats>({
    queryKey: ['kyc-stats'],
    queryFn: async () => {
      const response = await api.get('/admin/kyc/stats');
      return response.data;
    },
  });

  // Buscar contas aguardando submissão de KYC
  const { data: awaitingList, isLoading: isLoadingAwaiting } = useQuery<AwaitingSubmissionList>({
    queryKey: ['kyc-awaiting-submission', awaitingPage, search],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', awaitingPage.toString());
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      
      const response = await api.get(`/admin/kyc/awaiting-submission?${params.toString()}`);
      return response.data;
    },
    enabled: activeTab === 'awaiting',
  });

  // Mutation para aprovação em lote
  const batchApproveMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await api.post('/admin/kyc/batch-approve', { virtualAccountIds: ids });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-pending'] });
      queryClient.invalidateQueries({ queryKey: ['kyc-stats'] });
      setSelectedIds(new Set());
    },
  });

  // Mutation para rejeição em lote
  const batchRejectMutation = useMutation({
    mutationFn: async ({ ids, reason }: { ids: string[]; reason: string }) => {
      const response = await api.post('/admin/kyc/batch-reject', { virtualAccountIds: ids, reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kyc-pending'] });
      queryClient.invalidateQueries({ queryKey: ['kyc-stats'] });
      setSelectedIds(new Set());
      setShowBatchRejectModal(false);
      setBatchRejectReason('');
    },
  });

  const totalPages = Math.ceil((pendingList?.total || 0) / limit);
  const awaitingTotalPages = Math.ceil((awaitingList?.total || 0) / limit);

  // Toggle seleção de item
  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Selecionar/deselecionar todos
  const toggleSelectAll = () => {
    if (selectedIds.size === pendingList?.items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingList?.items.map(item => item.virtualAccountId) || []));
    }
  };

  // Verificar se todos estão selecionados
  const allSelected = pendingList?.items.length ? selectedIds.size === pendingList.items.length : false;

  // Itens selecionados (usado para referência futura em operações em lote)
  const _selectedItems = useMemo(() => {
    return pendingList?.items.filter(item => selectedIds.has(item.virtualAccountId)) || [];
  }, [pendingList?.items, selectedIds]);
  // Note: _selectedItems can be used for batch operations validation if needed

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Análise de KYC</h1>
          <p className="text-surface-400 mt-1">
            Gerencie as verificações de identidade dos correntistas
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 text-surface-400 hover:text-surface-100 hover:bg-surface-800 rounded-lg transition-colors"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard
          label="Aguardando"
          value={stats?.pending || 0}
          icon={Clock}
          color="text-yellow-400"
          bgColor="bg-yellow-500/20"
        />
        <StatCard
          label="Em Análise"
          value={stats?.inReview || 0}
          icon={AlertTriangle}
          color="text-blue-400"
          bgColor="bg-blue-500/20"
        />
        <StatCard
          label="Aprovados Hoje"
          value={stats?.approvedToday || 0}
          icon={CheckCircle2}
          color="text-green-400"
          bgColor="bg-green-500/20"
        />
        <StatCard
          label="Rejeitados Hoje"
          value={stats?.rejectedToday || 0}
          icon={XCircle}
          color="text-red-400"
          bgColor="bg-red-500/20"
        />
        <StatCard
          label="Reenvios"
          value={stats?.resubmissionsCount || 0}
          icon={RotateCcw}
          color="text-orange-400"
          bgColor="bg-orange-500/20"
        />
        <StatCard
          label="Mais Antigo"
          value={stats?.oldestPendingDays ? `${stats.oldestPendingDays}d` : '-'}
          icon={Calendar}
          color={stats?.oldestPendingDays && stats.oldestPendingDays > 7 ? 'text-red-400' : 'text-primary-400'}
          bgColor={stats?.oldestPendingDays && stats.oldestPendingDays > 7 ? 'bg-red-500/20' : 'bg-primary-500/20'}
          isText
        />
      </div>

      {/* SLA Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="glass rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-surface-400">SLA No Prazo</p>
              <p className="text-2xl font-bold text-green-400">{stats?.slaOnTimeCount || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>
        <div className="glass rounded-xl p-4 border border-yellow-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-surface-400">SLA em Alerta</p>
              <p className="text-2xl font-bold text-yellow-400">{stats?.slaWarningCount || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
        </div>
        <div className="glass rounded-xl p-4 border border-red-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-surface-400">SLA Estourado</p>
              <p className="text-2xl font-bold text-red-400">{stats?.slaBreachedCount || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </div>
        <div className="glass rounded-xl p-4 border border-primary-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-surface-400">Conformidade SLA</p>
              <p className="text-2xl font-bold text-primary-400">{stats?.avgSlaCompliancePercent || 0}%</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-primary-400" />
            </div>
          </div>
        </div>
        <div 
          className={cn(
            "glass rounded-xl p-4 border cursor-pointer transition-all hover:scale-[1.02]",
            (stats?.awaitingSubmission || 0) > 0 ? "border-orange-500/30" : "border-surface-700"
          )}
          onClick={() => setActiveTab('awaiting')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-surface-400">Aguardando Submissão</p>
              <p className={cn(
                "text-2xl font-bold",
                (stats?.awaitingSubmission || 0) > 0 ? "text-orange-400" : "text-surface-500"
              )}>
                {stats?.awaitingSubmission || 0}
              </p>
            </div>
            <div className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center",
              (stats?.awaitingSubmission || 0) > 0 ? "bg-orange-500/20" : "bg-surface-700"
            )}>
              <Inbox className={cn(
                "w-6 h-6",
                (stats?.awaitingSubmission || 0) > 0 ? "text-orange-400" : "text-surface-500"
              )} />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs de Navegação */}
      <div className="flex gap-2 border-b border-surface-700 pb-2">
        <button
          onClick={() => setActiveTab('pending')}
          className={cn(
            "px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2",
            activeTab === 'pending'
              ? "bg-primary-500/20 text-primary-400 border-b-2 border-primary-500"
              : "text-surface-400 hover:text-surface-100 hover:bg-surface-800"
          )}
        >
          <FileText className="w-4 h-4" />
          KYCs Pendentes
          {(stats?.pending || 0) > 0 && (
            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
              {stats?.pending}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('awaiting')}
          className={cn(
            "px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2",
            activeTab === 'awaiting'
              ? "bg-primary-500/20 text-primary-400 border-b-2 border-primary-500"
              : "text-surface-400 hover:text-surface-100 hover:bg-surface-800"
          )}
        >
          <Inbox className="w-4 h-4" />
          Aguardando Submissão
          {(stats?.awaitingSubmission || 0) > 0 && (
            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
              {stats?.awaitingSubmission}
            </span>
          )}
        </button>
      </div>

      {/* Filtros - Apenas para tab de pendentes */}
      {activeTab === 'pending' && (
      <div className="glass rounded-xl p-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Busca */}
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
            <input
              type="text"
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-primary-500"
            />
          </div>

          {/* Filtro de Status */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-40 px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
          >
            <option value="">Todos os Status</option>
            <option value="SUBMITTED">Aguardando</option>
            <option value="PENDING_REVIEW">Em Análise</option>
          </select>

          {/* Filtro de Tipo */}
          <select
            value={kycTypeFilter}
            onChange={(e) => setKycTypeFilter(e.target.value)}
            className="w-40 px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
          >
            <option value="">Todos os Tipos</option>
            <option value="ORIGIN">KYC de Origem</option>
            <option value="DESTINATION">KYC de Destino</option>
          </select>

          {/* Filtro de SLA */}
          <select
            value={slaFilter}
            onChange={(e) => setSlaFilter(e.target.value)}
            className="w-40 px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
          >
            <option value="">Todos os SLAs</option>
            <option value="ON_TIME">No Prazo</option>
            <option value="WARNING">Em Alerta</option>
            <option value="BREACHED">Estourado</option>
          </select>

          {/* Filtro de Prioridade */}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="w-44 px-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:border-primary-500"
          >
            <option value="">Todas Prioridades</option>
            <option value="URGENT">Urgente</option>
            <option value="HIGH">Alta</option>
            <option value="NORMAL">Normal</option>
            <option value="LOW">Baixa</option>
          </select>
        </div>
      </div>
      )}

      {/* Busca para tab de aguardando submissão */}
      {activeTab === 'awaiting' && (
        <div className="glass rounded-xl p-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
            <input
              type="text"
              placeholder="Buscar por nome, email ou documento..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-primary-500"
            />
          </div>
        </div>
      )}

      {/* Barra de Ações em Lote - Apenas para tab de pendentes */}
      {activeTab === 'pending' && selectedIds.size > 0 && (
        <div className="glass rounded-xl p-4 border border-primary-500/50 bg-primary-500/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-primary-400 font-medium">
                {selectedIds.size} selecionado{selectedIds.size > 1 ? 's' : ''}
              </span>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-sm text-surface-400 hover:text-surface-100"
              >
                Limpar seleção
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => batchApproveMutation.mutate(Array.from(selectedIds))}
                disabled={batchApproveMutation.isPending}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {batchApproveMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                Aprovar Todos
              </button>
              <button
                onClick={() => setShowBatchRejectModal(true)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Rejeitar Todos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Grid de Cards - Tab de KYCs Pendentes */}
      {activeTab === 'pending' && (
      <div className="space-y-4">
        {/* Header com seleção */}
        {pendingList?.items && pendingList.items.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 text-surface-400 hover:text-surface-100 transition-colors"
            >
              {allSelected ? (
                <CheckSquare className="w-5 h-5 text-primary-400" />
              ) : (
                <Square className="w-5 h-5" />
              )}
              <span className="text-sm">Selecionar todos</span>
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-surface-400 mt-2">Carregando...</p>
          </div>
        ) : pendingList?.items.length === 0 ? (
          <div className="glass rounded-xl p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-surface-400">Nenhum KYC pendente de análise</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingList?.items.map((item) => (
              <KycCard
                key={item.id}
                item={item}
                isSelected={selectedIds.has(item.virtualAccountId)}
                onToggleSelect={() => toggleSelection(item.virtualAccountId)}
              />
            ))}
          </div>
        )}

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4">
            <p className="text-sm text-surface-400">
              Mostrando {((page - 1) * limit) + 1} a {Math.min(page * limit, pendingList?.total || 0)} de {pendingList?.total || 0}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 bg-surface-800 text-surface-400 rounded-lg hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 bg-surface-800 text-surface-400 rounded-lg hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Grid de Cards - Tab de Aguardando Submissão */}
      {activeTab === 'awaiting' && (
        <div className="space-y-4">
          {isLoadingAwaiting ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-surface-400 mt-2">Carregando...</p>
            </div>
          ) : awaitingList?.items.length === 0 ? (
            <div className="glass rounded-xl p-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-surface-400">Nenhuma conta aguardando submissão de KYC</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {awaitingList?.items.map((item) => (
                <AwaitingSubmissionCard key={item.virtualAccountId} item={item} />
              ))}
            </div>
          )}

          {/* Paginação */}
          {awaitingTotalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-surface-400">
                Mostrando {((awaitingPage - 1) * limit) + 1} a {Math.min(awaitingPage * limit, awaitingList?.total || 0)} de {awaitingList?.total || 0}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setAwaitingPage(p => Math.max(1, p - 1))}
                  disabled={awaitingPage === 1}
                  className="px-3 py-1 bg-surface-800 text-surface-400 rounded-lg hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setAwaitingPage(p => Math.min(awaitingTotalPages, p + 1))}
                  disabled={awaitingPage === awaitingTotalPages}
                  className="px-3 py-1 bg-surface-800 text-surface-400 rounded-lg hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de Rejeição em Lote */}
      {showBatchRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">
              Rejeitar {selectedIds.size} KYC{selectedIds.size > 1 ? 's' : ''}
            </h3>
            <p className="text-surface-400 mb-4">
              Informe o motivo da rejeição. Todos os correntistas selecionados serão notificados.
            </p>
            <textarea
              value={batchRejectReason}
              onChange={(e) => setBatchRejectReason(e.target.value)}
              placeholder="Motivo da rejeição (mínimo 10 caracteres)"
              className="w-full p-3 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:border-primary-500 resize-none h-32"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowBatchRejectModal(false);
                  setBatchRejectReason('');
                }}
                className="px-4 py-2 text-surface-400 hover:text-surface-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => batchRejectMutation.mutate({ 
                  ids: Array.from(selectedIds), 
                  reason: batchRejectReason 
                })}
                disabled={batchRejectReason.length < 10 || batchRejectMutation.isPending}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {batchRejectMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KycCard({
  item,
  isSelected,
  onToggleSelect,
}: {
  item: KycPendingItem;
  isSelected: boolean;
  onToggleSelect: () => void;
}) {
  const statusCfg = statusConfig[item.status] || statusConfig.SUBMITTED;
  const isOld = item.waitingDays > 7;
  const isUrgent = item.waitingDays > 3;

  // SLA status colors
  const slaColors = {
    ON_TIME: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/50' },
    WARNING: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/50' },
    BREACHED: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/50' },
  };

  // Priority colors
  const priorityColors = {
    URGENT: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Urgente' },
    HIGH: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Alta' },
    NORMAL: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Normal' },
    LOW: { bg: 'bg-surface-500/20', text: 'text-surface-400', label: 'Baixa' },
  };

  const slaStyle = item.slaStatus ? slaColors[item.slaStatus] : slaColors.ON_TIME;
  const priorityStyle = item.priority ? priorityColors[item.priority] : priorityColors.NORMAL;

  return (
    <div
      className={cn(
        "glass rounded-xl p-4 border-2 transition-all",
        isSelected ? "border-primary-500 bg-primary-500/5" : 
        item.slaStatus === 'BREACHED' ? "border-red-500/70 bg-red-500/5" :
        item.slaStatus === 'WARNING' ? "border-yellow-500/50 bg-yellow-500/5" :
        statusCfg.borderColor
      )}
    >
      {/* Header com checkbox, prioridade e tipo */}
      <div className="flex items-start justify-between mb-3">
        <button
          onClick={onToggleSelect}
          className="flex items-center gap-2 text-surface-400 hover:text-surface-100 transition-colors"
        >
          {isSelected ? (
            <CheckSquare className="w-5 h-5 text-primary-400" />
          ) : (
            <Square className="w-5 h-5" />
          )}
        </button>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {item.priority && item.priority !== 'NORMAL' && (
            <span className={cn(
              "px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1",
              priorityStyle.bg, priorityStyle.text
            )}>
              <Zap className="w-3 h-3" />
              {priorityStyle.label}
            </span>
          )}
          {item.isResubmission && (
            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full flex items-center gap-1">
              <RotateCcw className="w-3 h-3" />
              Reenvio {item.resubmissionCount && item.resubmissionCount > 1 ? `(${item.resubmissionCount}x)` : ''}
            </span>
          )}
          <span className={cn(
            "px-2 py-0.5 rounded-full text-xs font-medium",
            item.kycType === 'ORIGIN' ? 'bg-purple-500/20 text-purple-400' : 'bg-cyan-500/20 text-cyan-400'
          )}>
            {getKycTypeLabel(item.kycType)}
          </span>
        </div>
      </div>

      {/* Dados do usuário */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-surface-400" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-surface-100 truncate">{item.userName}</p>
          <p className="text-sm text-surface-500 truncate">{item.userEmail}</p>
        </div>
      </div>

      {/* Analista atribuído */}
      {item.assignedAnalystName && (
        <div className="flex items-center gap-2 mb-3 text-sm">
          <User className="w-4 h-4 text-primary-400" />
          <span className="text-surface-400">Analista:</span>
          <span className="text-surface-200">{item.assignedAnalystName}</span>
        </div>
      )}

      {/* Status e documentos */}
      <div className="flex items-center justify-between mb-3">
        <KycStatusBadge status={item.status === 'SUBMITTED' ? 'PENDING' : 'IN_REVIEW'} size="sm" />
        <div className="flex items-center gap-1 text-surface-400 text-sm">
          <FileText className="w-4 h-4" />
          <span>{item.documentsCount} doc{item.documentsCount !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* SLA Indicator */}
      {item.slaStatus && (
        <div className={cn(
          "flex items-center justify-between p-2 rounded-lg mb-3 border",
          slaStyle.bg, slaStyle.border
        )}>
          <div className="flex items-center gap-2">
            <Timer className={cn("w-4 h-4", slaStyle.text)} />
            <span className={cn("text-sm font-medium", slaStyle.text)}>
              {item.slaStatus === 'ON_TIME' && 'SLA no prazo'}
              {item.slaStatus === 'WARNING' && 'SLA em alerta'}
              {item.slaStatus === 'BREACHED' && 'SLA estourado'}
            </span>
          </div>
          {item.slaRemainingHours !== undefined && item.slaStatus !== 'BREACHED' && (
            <span className={cn("text-xs", slaStyle.text)}>
              {item.slaRemainingHours}h restantes
            </span>
          )}
        </div>
      )}

      {/* Tempo de espera */}
      <div className={cn(
        "flex items-center justify-between p-2 rounded-lg",
        isOld ? "bg-red-500/10" : isUrgent ? "bg-yellow-500/10" : "bg-surface-800"
      )}>
        <div className="flex items-center gap-2">
          <Clock className={cn(
            "w-4 h-4",
            isOld ? "text-red-400" : isUrgent ? "text-yellow-400" : "text-surface-400"
          )} />
          <span className={cn(
            "text-sm",
            isOld ? "text-red-400 font-medium" : isUrgent ? "text-yellow-400" : "text-surface-400"
          )}>
            {item.waitingTime}
          </span>
        </div>
        {isOld && (
          <AlertTriangle className="w-4 h-4 text-red-400" />
        )}
      </div>

      {/* Botão de ação */}
      <Link
        to={`/admin/kyc/${item.virtualAccountId}`}
        className="mt-3 w-full inline-flex items-center justify-center gap-1 px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm"
      >
        Analisar
        <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
  bgColor,
  isText = false,
}: {
  label: string;
  value: number | string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  isText?: boolean;
}) {
  return (
    <div className="glass rounded-xl p-3">
      <div className="flex items-center gap-2">
        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", bgColor)}>
          <Icon className={cn("w-4 h-4", color)} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-surface-400 truncate">{label}</p>
          <p className={cn("text-lg font-bold", color)}>
            {isText ? value : typeof value === 'number' ? value.toLocaleString('pt-BR') : value}
          </p>
        </div>
      </div>
    </div>
  );
}

function AwaitingSubmissionCard({ item }: { item: AwaitingSubmissionItem }) {
  const formatDocument = (doc: string) => {
    if (!doc) return '-';
    if (doc.length === 11) {
      return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (doc.length === 14) {
      return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return doc;
  };

  const getContractStatusLabel = (status: string) => {
    const labels: Record<string, { label: string; color: string; bgColor: string }> = {
      PENDING_APPROVAL: { label: 'Aguardando Aprovação', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' },
      PENDING_KYC: { label: 'Aguardando KYC', color: 'text-orange-400', bgColor: 'bg-orange-500/20' },
      ACTIVE: { label: 'Ativo', color: 'text-green-400', bgColor: 'bg-green-500/20' },
      BLOCKED: { label: 'Bloqueado', color: 'text-red-400', bgColor: 'bg-red-500/20' },
    };
    return labels[status] || { label: status, color: 'text-surface-400', bgColor: 'bg-surface-500/20' };
  };

  const contractStatus = getContractStatusLabel(item.contractStatus);

  return (
    <div className="glass rounded-xl p-4 border-2 border-orange-500/30 bg-orange-500/5">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileQuestion className="w-5 h-5 text-orange-400" />
          <span className="text-sm font-medium text-orange-400">Aguardando Submissão</span>
        </div>
        <span className={cn(
          "px-2 py-0.5 rounded-full text-xs font-medium",
          contractStatus.bgColor, contractStatus.color
        )}>
          {contractStatus.label}
        </span>
      </div>

      {/* Dados do usuário */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-surface-700 flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-surface-400" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-surface-100 truncate">{item.userName}</p>
          <p className="text-sm text-surface-500 truncate">{item.userEmail}</p>
        </div>
      </div>

      {/* Documento */}
      <div className="flex items-center gap-2 mb-3 text-sm">
        <FileText className="w-4 h-4 text-surface-400" />
        <span className="text-surface-400">Documento:</span>
        <span className="text-surface-200 font-mono">{formatDocument(item.userDocument)}</span>
      </div>

      {/* Status de KYC */}
      <div className="space-y-2 mb-3">
        <div className="flex items-center justify-between p-2 rounded-lg bg-surface-800">
          <span className="text-sm text-surface-400">KYC Origem</span>
          <span className={cn(
            "text-sm font-medium",
            item.hasOriginSubmission ? "text-green-400" : "text-orange-400"
          )}>
            {item.hasOriginSubmission ? "Submetido" : "Pendente"}
          </span>
        </div>
        <div className="flex items-center justify-between p-2 rounded-lg bg-surface-800">
          <span className="text-sm text-surface-400">KYC Destino</span>
          <span className={cn(
            "text-sm font-medium",
            item.hasDestinationSubmission ? "text-green-400" : "text-orange-400"
          )}>
            {item.hasDestinationSubmission ? "Submetido" : "Pendente"}
          </span>
        </div>
      </div>

      {/* Tempo de espera */}
      <div className={cn(
        "flex items-center justify-between p-2 rounded-lg",
        item.waitingDays > 7 ? "bg-red-500/10" : item.waitingDays > 3 ? "bg-yellow-500/10" : "bg-surface-800"
      )}>
        <div className="flex items-center gap-2">
          <Clock className={cn(
            "w-4 h-4",
            item.waitingDays > 7 ? "text-red-400" : item.waitingDays > 3 ? "text-yellow-400" : "text-surface-400"
          )} />
          <span className={cn(
            "text-sm",
            item.waitingDays > 7 ? "text-red-400 font-medium" : item.waitingDays > 3 ? "text-yellow-400" : "text-surface-400"
          )}>
            {item.waitingTime}
          </span>
        </div>
        {item.waitingDays > 7 && (
          <AlertTriangle className="w-4 h-4 text-red-400" />
        )}
      </div>

      {/* Mensagem informativa */}
      <div className="mt-3 p-2 rounded-lg bg-orange-500/10 border border-orange-500/30">
        <p className="text-xs text-orange-400">
          O correntista ainda não enviou os documentos de KYC. Aguardando submissão pelo app/portal.
        </p>
      </div>
    </div>
  );
}

export default KycQueue;
